import { setClass, translate } from '@/lib/dom'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FOKUS MÜHƏRRİKİ (spatial navigation) — DOM-birbaşa, sıfır reaktivlik
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * NİYƏ HAZIR KİTABXANA DEYİL. `@noriginmedia/norigin-spatial-navigation` və
 * bənzərləri fokusu HƏNDƏSİ olaraq tapır: hər addımda namizəd elementlərin
 * `getBoundingClientRect()`-i oxunur. Bir oxumaq = MƏCBURİ LAYOUT (forced
 * reflow). 60 kartlıq ekranda bir ox basışı 60 layout deməkdir; köhnə TV-də
 * bu, 200-500 ms-dir. Basılı saxlananda ekran tam donur.
 *
 * BURADAKI MODEL. Ekran «sətir × sütun» indeks şəbəkəsidir. Hərəkət SAF
 * ARİFMETİKADIR — bir dənə də ölçü oxunmur:
 *
 *     sağ  → col + 1                sürüşmə: -(col − LEAD) × pitch
 *     aşağı→ row + 1                sürüşmə: -(rowTop[row] − KEEP_TOP)
 *
 * Bir addımda DOM-a toxunuş sayı: 2 sinif dəyişikliyi + 2 `transform` yazısı.
 * Layout oxunuşu: 0.
 *
 * ⚠ `element.focus()` ÇAĞIRILMIR. Doğma fokus köhnə TV-də avtomatik
 * `scrollIntoView` işlədir — yenə məcburi layout. Fokus tamamilə vizualdır
 * (`.is-focused` sinfi), giriş isə onsuz da `document`-dən gəlir.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type Direction = 'up' | 'down' | 'left' | 'right'

/** Fokuslana bilən bir element və onun «OK» aksiyası. */
export interface FocusItem {
  node: HTMLElement
  /** Enter basılanda. */
  onEnter?: () => void
  /** Sətir/kart məlumatı — ekran öz kontekstini burada saxlayır. */
  data?: unknown
}

/** Bir sıra fokus elementi (sol/sağ hərəkət bu sıra daxilindədir). */
export interface FocusRow {
  items: FocusItem[]
  /** Üfüqi sürüşən konteyner (sıra track-i). Yoxdursa sıra sürüşmür. */
  track?: HTMLElement
  /** Bir elementin en addımı (kart eni + boşluq). Track varsa məcburidir. */
  pitch?: number
  /** Eyni anda görünən element sayı — sürüşmə həddi bununla hesablanır. */
  visible?: number
  /** Bu sətrin şaquli scroller içindəki `top` koordinatı. */
  top?: number
  /** Sətrə fokus düşəndə (lazy şəkil yükləmək üçün). */
  onEnterRow?: (row: number) => void
}

/** Fokus şəbəkəsinin qurulma parametrləri. */
export interface FocusGridOptions {
  /** Şaquli sürüşən konteyner (adətən `.scroller`). */
  scroller?: HTMLElement
  /** Fokuslu sətir ekranın bu `y`-sindən yuxarı qalxmır. */
  keepTop?: number
  /** Şaquli sürüşmənin maksimum məsafəsi. */
  maxScroll?: number
  /** Sol kənardan çıxmaq istəyəndə (rail-a keçid). `true` qaytarsa hərəkət udulur. */
  onExitLeft?: () => boolean
  /** Fokus dəyişdikdə — ekran hero/preview yeniləyir. */
  onFocusChange?: (row: number, col: number, item: FocusItem) => void
}

/** Fokuslu kart sol kənardan neçə kart aralı saxlanılır. */
const LEAD = 1

/**
 * QLOBAL FOKUS MÜŞAHİDƏÇİSİ — kart öngörünümü üçün (MYTasks 586).
 *
 * ⚠ NİYƏ QLOBAL, HƏR EKRANIN `onFocusChange`-i DEYİL: kartlar ALTI ekranda
 *   qurulur (ana · kəşf · siyahı · axtarış · tezliklə · detal) və hər birində
 *   `FocusGrid` ayrıca yaradılır. Nüsxə-nüsxə qoşulsaydı biri unudulanda
 *   öngörünüm həmin ekranda səssizcə işləməzdi — üstəlik `onFocusChange`-i
 *   ekranların özü hero/preview üçün işlədir, yəni sahə onsuz da tutulub.
 * ⚠ XƏRC: fokus addımına BİR funksiya çağırışı (ölçü oxunuşu yoxdur). Addımın
 *   qalan işi dəyişmir — PERFORMANCE.md §3 qaydası pozulmur.
 * ⚠ `blur()` DƏ XƏBƏR VERİR (`null` ilə): ekran söküləndə, modal açılanda və
 *   oynadıcıya keçəndə öngörünüm MÜTLƏQ dayanmalıdır (iki video paralel dekod =
 *   köhnə TV-də donma, PERFORMANCE.md §4).
 */
let focusWatcher: ((node: HTMLElement | null) => void) | null = null

/** Fokus dəyişikliyini izləyəni qeyd edir (`main.ts` boot-da bir dəfə). */
export function setFocusWatcher(watcher: (node: HTMLElement | null) => void): void {
  focusWatcher = watcher
}

/**
 * FOKUSU SON DƏFƏ ƏLİNDƏ TUTAN ŞƏBƏKƏ — siçan qatının hədəfi (MYTasks, 2026-08-10).
 *
 * ⚠⚠ NİYƏ MODUL SƏVİYYƏSİNDƏ REYESTR, `Screen` MÜQAVİLƏSİNƏ YENİ METOD DEYİL:
 *    hər ekran öz `FocusGrid`-ini PRIVATE saxlayır (`screens/*.ts`), yəni siçan
 *    hadisəsini ekrana ötürmək üçün 12 ekranın hamısına eyni metod yazılmalı
 *    olardı. Bu isə MÜVƏQQƏTİ tədbirdir (sahib: «hələlik qaldır, sonra
 *    bloklayarıq») — geri qaytaranda 12 fayla toxunmaq lazım gəlməsin deyə
 *    qeydiyyat bir yerdədir. Naxış `focusWatcher` ilə eynidir (yuxarıda).
 * ⚠ SON FOKUSLANAN QALİBDİR: modal açılanda onun şəbəkəsi fokusu alır və
 *   avtomatik olaraq aktiv olur, bağlananda isə arxadakı ekran `takeFocus()`
 *   çağırır və reyestr ona qayıdır — ayrıca «kim üstdədir» hesabatına ehtiyac yoxdur.
 */
let activeGrid: FocusGrid | null = null

/**
 * Reyestri yeniləyən köməkçi.
 *
 * ⚠ NİYƏ FUNKSİYA, `activeGrid = this` DEYİL (2026-08-16 — TestingClaude veb TV 3):
 *   oxlint `typescript(no-this-alias)` ilə DƏQİQ 1 xəbərdarlıq verirdi
 *   (`src/lib/focus.ts:209`) — qayda `this`-in dəyişənə mənimsədilməsini tutur.
 *   Davranış eynidir (eyni sətir, eyni an), yalnız mənimsətmə modul funksiyasının
 *   içindədir: `this` arqument kimi ötürülür, dəyişənə yazılmır. Rəsmi susdurma
 *   şərhi (`oxlint-disable`) qoyulmadı — o, qaydanı bu fayl üçün kor edərdi.
 */
function markActiveGrid(grid: FocusGrid): void {
  activeGrid = grid
}

/** Siçan/toxunuş hadisəsini AKTİV şəbəkəyə ötürür. Hədəf şəbəkəyə aid deyilsə `false`. */
export function pointerToActiveGrid(target: EventTarget | null, activate: boolean): boolean {
  if (!activeGrid) return false
  return activeGrid.pointer(target, activate)
}

function notifyWatcher(node: HTMLElement | null): void {
  if (!focusWatcher) return
  try {
    focusWatcher(node)
  } catch (error) {
    // Öngörünümün xətası naviqasiyanı öldürməməlidir.
    // eslint-disable-next-line no-console
    console.error('[tv] fokus müşahidəçisi xətası', error)
  }
}

/**
 * Pult fokusunun mühərriki.
 * ⚠ ÖLÇÜ OXUNMUR: `getBoundingClientRect`/`elementFromPoint` işlədilmir — mövqe
 * arifmetika ilə hesablanır. Köhnə TV-də məcburi layout kadr büdcəsini yeyir
 * (bax `PERFORMANCE.md`).
 */
export class FocusGrid {
  private rows: FocusRow[] = []
  private options: FocusGridOptions
  private row = 0
  private col = 0
  private active: HTMLElement | null = null

  constructor(options: FocusGridOptions = {}) {
    this.options = options
  }

  addRow(row: FocusRow): number {
    this.rows.push(row)
    return this.rows.length - 1
  }

  /** Bütün sıralar əlavə olunandan sonra məzmun hündürlüyü bilinir. */
  setMaxScroll(value: number): void {
    this.options.maxScroll = Math.max(0, value)
  }

  rowCount(): number {
    return this.rows.length
  }

  isEmpty(): boolean {
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i]
      if (row && row.items.length > 0) return false
    }
    return true
  }

  current(): FocusItem | null {
    const row = this.rows[this.row]
    if (!row) return null
    return row.items[this.col] || null
  }

  currentIndex(): { row: number; col: number } {
    return { row: this.row, col: this.col }
  }

  /**
   * Fokusu göstərilən xanaya qoyur. Yeganə DOM mutasiya nöqtəsi.
   *
   * ⚠ `viaPointer` (2026-08-02 düzəlişi — «mouse ilə sürüşmə çox kəskindir»):
   *   siçan/kursor kartın ÜSTÜNƏ gəldiyi üçün çağırılır, ona görə həmin kart
   *   ARTIQ GÖRÜNƏNDİR (görünməyən kart üzərinə `mouseover` fiziki mümkün
   *   deyil). Pultda isə `LEAD`-ə görə hər addımda sətir YENİDƏN mərkəzləşir
   *   (10-foot naviqasiyada gözlənilən davranış). Eyni məntiqi siçana da tətbiq
   *   etsək, ekranda onsuz da görünən kartın üstünə gələndə belə bütün sıra
   *   sürüşürdü — sürətli keçiddə bu, ard-arda «ani tullanma» kimi görünürdü.
   *   Siçan yolunda sürüşmə TAMAMILƏ ötürülür — yalnız fokus halqası köçür.
   */
  focus(rowIndex: number, colIndex: number, silent?: boolean, viaPointer?: boolean): void {
    const row = this.rows[rowIndex]
    if (!row || row.items.length === 0) return

    const col = clamp(colIndex, 0, row.items.length - 1)
    const item = row.items[col]
    if (!item) return

    if (this.active && this.active !== item.node) setClass(this.active, 'is-focused', false)
    setClass(item.node, 'is-focused', true)
    this.active = item.node

    const changedRow = this.row !== rowIndex
    this.row = rowIndex
    this.col = col

    if (!viaPointer) {
      this.slideRow(row, col)
      if (changedRow) this.slideScroller(row)
    }
    if (changedRow && row.onEnterRow) row.onEnterRow(rowIndex)
    if (!silent && this.options.onFocusChange) this.options.onFocusChange(rowIndex, col, item)
    // Siçan qatının hədəfi — izah `activeGrid` bəyanındadır.
    markActiveGrid(this)
    notifyWatcher(item.node)
  }

  /** Ekran açılanda ilk dolu sətrə fokus verir. */
  focusFirst(): void {
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i]
      if (row && row.items.length > 0) {
        this.focus(i, 0)
        return
      }
    }
  }

  /** `false` → bu istiqamətdə gedəcək yer yoxdur; ekran özü qərar verir. */
  move(direction: Direction): boolean {
    const row = this.rows[this.row]
    if (!row) return false

    if (direction === 'left') {
      if (this.col > 0) {
        this.focus(this.row, this.col - 1)
        return true
      }
      if (this.options.onExitLeft) return this.options.onExitLeft()
      return false
    }

    if (direction === 'right') {
      if (this.col < row.items.length - 1) {
        this.focus(this.row, this.col + 1)
        return true
      }
      return false
    }

    const step = direction === 'down' ? 1 : -1
    let next = this.row + step
    // Boş sətirləri atlayır (məs. gizlədilmiş sıra).
    while (next >= 0 && next < this.rows.length) {
      const candidate = this.rows[next]
      if (candidate && candidate.items.length > 0) {
        // ⚠ ŞAQULİ KEÇİD HƏMİŞƏ BİRİNCİ KARTA DÜŞÜR (istifadəçi tələbi,
        //   2026-07-29). Əvvəl «arzu olunan sütun» yadda saxlanılırdı (masaüstü
        //   şəbəkə naxışı): üçüncü kartda dayanıb aşağı basanda alt sıra da
        //   üçüncü kartdan açılır və sıranın əvvəli GÖRÜNMÜR — TV-də istifadəçi
        //   yeni sıraya baxmaq üçün əvvəlcə sola qayıtmalı olurdu. İndi sütun
        //   sıfırlanır, `slideRow` də track-i başa qaytarır.
        this.focus(next, 0)
        return true
      }
      next += step
    }
    return false
  }

  enter(): void {
    const item = this.current()
    if (item && item.onEnter) item.onEnter()
  }

  /**
   * Verilmiş DOM elementinin şəbəkədəki yerini tapır (yoxdursa `null`).
   *
   * SİÇAN DƏSTƏYİ üçündür: kursor bir kartın üstünə gələndə/klikləyəndə həmin
   * kartın (row, col) indeksi lazımdır.
   *
   * ⚠ ÖLÇÜ OXUNMUR — sadəcə saxlanmış `node` istinadları ilə müqayisə. Fokus
   *   mühərrikinin əsas qaydası (sıfır məcburi layout) pozulmur; axtarış ən pis
   *   halda ekrandakı element sayı qədərdir və YALNIZ siçan hadisəsində işləyir
   *   (pultla naviqasiyada heç vaxt çağırılmır).
   */
  locate(node: HTMLElement): { row: number; col: number } | null {
    for (let r = 0; r < this.rows.length; r++) {
      const items = (this.rows[r] as FocusRow).items
      for (let c = 0; c < items.length; c++) {
        if ((items[c] as FocusItem).node === node) return { row: r, col: c }
      }
    }
    return null
  }

  /**
   * Siçan hadisəsinin hədəfindən ən yaxın fokuslana bilən elementi tapıb fokusu
   * ora köçürür. `activate` verilibsə həm də «Enter» işlədir (klik).
   *
   * Hədəf şəbəkəyə aid deyilsə heç nə etmir və `false` qaytarır — çağıran
   * hadisəni öz üzərinə götürməsin.
   */
  pointer(target: EventTarget | null, activate: boolean): boolean {
    let node = target as HTMLElement | null
    // Kartın içindəki mətn/şəkil də klik hədəfi ola bilər — yuxarı qalxırıq.
    for (let depth = 0; node && depth < 8; depth++) {
      const found = this.locate(node)
      if (found) {
        // Artıq fokusdadırsa yenidən boyamırıq (lazımsız DOM yazısı olmasın).
        // `viaPointer=true` — sürüşmə YOXDUR (bax `focus()`-dakı qeyd).
        if (found.row !== this.row || found.col !== this.col) {
          this.focus(found.row, found.col, undefined, true)
        }
        if (activate) this.enter()
        return true
      }
      node = node.parentElement
    }
    return false
  }

  /** Ekran söndürülərkən halqanı təmizləyir. */
  blur(): void {
    if (this.active) setClass(this.active, 'is-focused', false)
    this.active = null
    // Fokus sahibi qalmadı — öngörünüm dayanmalıdır (yuxarıdakı qeyd).
    notifyWatcher(null)
  }

  reset(): void {
    this.blur()
    this.rows = []
    this.row = 0
    this.col = 0
  }

  // ── Sürüşmə — hamısı arifmetikadır, ölçü oxunmur ────────────────────────

  private slideRow(row: FocusRow, col: number): void {
    if (!row.track || !row.pitch) return
    const visible = row.visible || 6
    const maxIndex = Math.max(0, row.items.length - visible)
    const index = clamp(col - LEAD, 0, maxIndex)
    translate(row.track, -index * row.pitch, 0)
  }

  private slideScroller(row: FocusRow): void {
    const scroller = this.options.scroller
    if (!scroller || row.top === undefined) return
    const keepTop = this.options.keepTop === undefined ? 240 : this.options.keepTop
    const maxScroll = this.options.maxScroll === undefined ? 0 : this.options.maxScroll
    const offset = clamp(row.top - keepTop, 0, Math.max(0, maxScroll))
    translate(scroller, 0, -offset)
  }
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min
  if (value > max) return max
  return value
}
